/**
 * assets/bundle-builder.js
 * Tap/click select with visible +/- controls, running total, incomplete-
 * bundle prevention (Add to Cart stays disabled until exactly box_size
 * bags are picked), sessionStorage persistence (survives navigating away
 * and back while browsing), and a REAL multi-line /cart/add.js call
 * (Shopify's cart endpoint accepts an `items` array for exactly this).
 * Each line gets a `_bundle` property so order/cart data can identify
 * which lines belong to a given box.
 */
(function () {
  const root = document.querySelector("[data-bundle-builder]");
  if (!root) return;

  const settings = (window.driedYet && window.driedYet.settings) || {};
  const boxSize = parseInt(root.dataset.boxSize, 10) || 6;
  const boxDiscountPercent = parseFloat(root.dataset.boxDiscount) || 0;
  const storageKey = "driedyet_bundle_box";

  const pickCards = Array.from(root.querySelectorAll("[data-pick-card]"));
  const selectedCountEl = root.querySelector("[data-selected-count]");
  const remainingTextEl = root.querySelector("[data-remaining-text]");
  const lineListEl = root.querySelector("[data-bundle-line-list]");
  const valueEl = root.querySelector("[data-bundle-value]");
  const priceEl = root.querySelector("[data-bundle-price]");
  const savingsRow = root.querySelector("[data-bundle-savings-row]");
  const savingsEl = root.querySelector("[data-bundle-savings]");
  const addBtn = root.querySelector("[data-bundle-add-to-cart]");
  const errorBox = root.querySelector("[data-bundle-error]");

  function formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  function loadState() {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey)) || {};
    } catch (e) {
      return {};
    }
  }

  function saveState(state) {
    sessionStorage.setItem(storageKey, JSON.stringify(state));
  }

  let state = loadState(); // { [variantId]: count }

  function totalSelected() {
    return Object.values(state).reduce((sum, n) => sum + n, 0);
  }

  function totalValueCents() {
    return pickCards.reduce((sum, card) => {
      const count = state[card.dataset.variantId] || 0;
      return sum + count * parseInt(card.dataset.unitPrice, 10);
    }, 0);
  }

  function render() {
    const total = totalSelected();

    pickCards.forEach((card) => {
      const countEl = card.querySelector("[data-pick-count]");
      const count = state[card.dataset.variantId] || 0;
      countEl.textContent = count;
      card.classList.toggle("has-selection", count > 0);
      const plusBtn = card.querySelector("[data-pick-plus]");
      if (plusBtn) plusBtn.disabled = total >= boxSize && count === 0 ? true : total >= boxSize;
    });

    selectedCountEl.textContent = total;
    const remaining = boxSize - total;
    remainingTextEl.textContent =
      remaining > 0 ? `Add ${remaining} more to complete your box.` : "Your box is complete!";
    remainingTextEl.classList.toggle("is-complete", remaining <= 0);

    lineListEl.innerHTML = "";
    pickCards.forEach((card) => {
      const count = state[card.dataset.variantId] || 0;
      if (count > 0) {
        const li = document.createElement("li");
        li.textContent = `${card.dataset.productTitle} × ${count}`;
        lineListEl.appendChild(li);
      }
    });

    const value = totalValueCents();
    valueEl.textContent = formatMoney(value);

    const boxPriceCents = Math.round(value * (1 - boxDiscountPercent / 100));
    priceEl.textContent = formatMoney(boxPriceCents);
    const savings = value - boxPriceCents;
    if (savings > 0 && total === boxSize) {
      savingsRow.hidden = false;
      savingsEl.textContent = formatMoney(savings);
    } else {
      savingsRow.hidden = true;
    }

    const complete = total === boxSize;
    addBtn.disabled = !complete;
    addBtn.textContent = complete
      ? `Add Box to Cart — ${formatMoney(boxPriceCents)}`
      : `Select ${remaining} More Bag${remaining === 1 ? "" : "s"} to Continue`;

    saveState(state);
  }

  function changeCount(variantId, delta) {
    const total = totalSelected();
    const current = state[variantId] || 0;
    if (delta > 0 && total >= boxSize) return; // incomplete-bundle prevention (also prevents over-filling)
    const next = Math.max(0, current + delta);
    if (next === 0) delete state[variantId];
    else state[variantId] = next;
    render();
  }

  pickCards.forEach((card) => {
    const variantId = card.dataset.variantId;
    card.querySelector("[data-pick-plus]").addEventListener("click", () => changeCount(variantId, 1));
    card.querySelector("[data-pick-minus]").addEventListener("click", () => changeCount(variantId, -1));
    // Tapping the card itself (not just the + button) also adds one — larger, more discoverable hit target.
    card.addEventListener("click", (e) => {
      if (e.target.closest("[data-pick-plus], [data-pick-minus]")) return;
      changeCount(variantId, 1);
    });
  });

  addBtn.addEventListener("click", async () => {
    if (addBtn.disabled) return;
    errorBox.hidden = true;
    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = "Adding to cart…";

    const bundleId = `box-${Date.now()}`;
    const items = Object.entries(state).map(([variantId, quantity]) => ({
      id: variantId,
      quantity,
      properties: {
        _bundle: bundleId,
        _bundle_discount: `${boxDiscountPercent}%`,
      },
    }));

    try {
      const res = await fetch(settings.cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.description || "Couldn't add your box to the cart.");
      }

      document.dispatchEvent(
        new CustomEvent("dyet:bundle_complete", { detail: { bundle_id: bundleId, items, box_price: Math.round(totalValueCents() * (1 - boxDiscountPercent / 100)) } })
      );
      document.dispatchEvent(new CustomEvent("dyet:cart:added"));

      state = {};
      sessionStorage.removeItem(storageKey);
      render();

      addBtn.textContent = "Added to Cart ✓";
      setTimeout(render, 1800);
    } catch (err) {
      errorBox.textContent = err.message;
      errorBox.hidden = false;
      addBtn.disabled = false;
      addBtn.textContent = originalText;
    }
  });

  render();
})();
